import { getDeviceId } from '@/commonFunctions/device'
import { BASE_API_URL } from '@/constants/Common'
import {
  GetProfileResponse,
  GetUserInitializationResponse,
  GetUserStatsResponse,
  normalizeUserPreferences,
  PayloadResendVerifyPhone,
  PayloadResetPassword,
  PayloadUpdateProfile,
  PayloadVerifyPhone,
  Status,
  UserPreferences,
} from '@/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import httpService from './httpService'

// Local, envelope-aware types for the user-settings schema. We intentionally do NOT import the
// generated model: the main app consumes the generated code as a compiled project reference
// (src/app/commons/generated/lib), and these newer schema models have no committed lib output, so
// importing them breaks `tsc`. The generated response type also models the unwrapped payload, but
// the endpoint returns the standard `{ result }` envelope — handled by unwrapping below.
export type UserSettingValueType = 'BOOLEAN' | 'STRING' | 'TIMESTAMP'
export type UserSettingFieldSchema = {
  key: string
  valueType: UserSettingValueType
  description: string
  subText?: string
  defaultValue: string
}
export type UserSettingsCategorySchema = {
  category: string
  displayName: string
  settings: UserSettingFieldSchema[]
}
export type UserSettingsSchema = { categories: UserSettingsCategorySchema[] }

const userPath = '/user/v1.0'
const getProfilePath = `${userPath}/profile`
const getUserSettingsPath = `${userPath}/settings`
const getUserSettingsSchemaPath = `${getUserSettingsPath}/schema`
const userStatsPath = 'user/calculated/v1.0/stats'
const verifyPhonePath = 'user/phone/v1.0/verify'
const resendVerifyPhonePath = 'user/phone/v1.0/resend'
const resetPasswordPath = 'user/password/v1.0/reset'
const userInitializationPath = `${userPath}/initialization`
const userVerifyEmailPath = '/user/email/verify/v1.1/initiate'

export enum UserQueryKeys {
  GET_STATS = 'get_stats',
  GET_PLAYER_INITIALIZATION = 'get_player_initialization',
  GET_SETTINGS_SCHEMA = 'get_settings_schema',
  GET_SETTINGS = 'get_settings',
}

export const logout = async () => {
  const payload = {
    platform: 'web',
    uuid: getDeviceId(),
  }
  return fetch(`${BASE_API_URL}user/v1.0/logout`, {
    method: 'post',
    // Authenticated by the HttpOnly session cookie (sent because of credentials: 'include'); the
    // server also clears the cookie on this call. No bearer to pass.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
}

const getUserPreferences = async (): Promise<UserPreferences> => {
  const res = (await httpService.Get(getUserSettingsPath)) as { result: UserPreferences }

  return normalizeUserPreferences(res.result)
}

export const getProfile = async () => {
  const res = await httpService.Get<GetProfileResponse>(getProfilePath)
  res.result.userPreferences = await getUserPreferences()
  return { ...res }
}

export const useMutateUserSettings = () => {
  return useMutation(async (payload: UserPreferences) => {
    const res = await httpService.Put(getUserSettingsPath, { settings: payload })
    return res
  })
}

/**
 * Fetches the self-describing user-settings schema (categories + fields with
 * value types, descriptions, and defaults). Drives the dynamic preferences UI
 * so new backend settings appear without a frontend change.
 */
export const useUserSettingsSchema = () => {
  return useQuery(
    [UserQueryKeys.GET_SETTINGS_SCHEMA],
    async () => {
      const res = await httpService.Get<{ result: UserSettingsSchema }>(getUserSettingsSchemaPath)
      return res.result
    },
    {
      staleTime: 60 * 60 * 1000,
    }
  )
}

/**
 * Fetches the raw user-settings values map (key -> stored string value). Unlike
 * `getProfile().userPreferences`, this keeps the wire string values intact so the
 * schema-driven page can interpret them by each setting's declared value type.
 */
export const useUserSettings = () => {
  return useQuery([UserQueryKeys.GET_SETTINGS], async () => {
    const res = (await httpService.Get(getUserSettingsPath)) as { result: Record<string, string> }
    return res.result ?? {}
  })
}

/**
 * The envelope a settings write answers with. `PARTIAL` means the write landed but not all of it:
 * the endpoint skips keys that have their own dedicated endpoint (`hiddenBadges` goes through
 * `POST /badges/{urn}/visibility`) rather than failing everything sent alongside them, and
 * `message` names what it left out.
 */
export type UserSettingsWriteResult = { status?: string; message?: string }

/**
 * Mutation that persists a raw settings map ({ key: stringValue }) via PUT /user/settings,
 * matching the backend `{ settings: { key: value } }` contract. Used by the schema-driven page.
 */
export const useMutateUserSettingsRaw = () => {
  return useMutation(async (settings: Record<string, string>) => {
    const res = await httpService.Put<UserSettingsWriteResult>(getUserSettingsPath, { settings })
    return res
  })
}

export const useUpdateProfile = () => {
  return useMutation(async (payload: PayloadUpdateProfile) => {
    const res = await httpService.Put(getProfilePath, payload)

    // Will throw error if response is not 200
    if (payload.userPreferences) {
      await httpService.Put(getUserSettingsPath, payload.userPreferences)
    }

    res.result.userPreferences = await getUserPreferences()
    return { ...res }
  })
}

export const useVerifyPhone = () => {
  return useMutation(async (payload: PayloadVerifyPhone) => {
    const res = await httpService.Post(verifyPhonePath, payload)
    return res
  })
}

export const useResendVerifyPhone = () => {
  return useMutation(async (payload: PayloadResendVerifyPhone) => {
    const res = await httpService.Post(resendVerifyPhonePath, payload)
    return res
  })
}

export const useResetPassword = () => {
  return useMutation(async (payload: PayloadResetPassword) => {
    const res = await httpService.Post(resetPasswordPath, payload)
    return res
  })
}

export const useGetUserStats = (userId: number) => {
  return useQuery(
    [UserQueryKeys.GET_STATS, userId],
    async () => {
      const res = await httpService.Get<GetUserStatsResponse>(`${userStatsPath}/${userId}`)
      return res.result
    },
    {
      enabled: !!userId,
    }
  )
}

export const useGetPlayerInitialization = (userId: number, enabled: boolean, initialization: boolean) => {
  return useQuery(
    [UserQueryKeys.GET_PLAYER_INITIALIZATION, userId],
    async () => {
      const res = await httpService.Get<GetUserInitializationResponse>(`${userInitializationPath}/${userId}`)
      return res.results
    },
    {
      enabled: !!userId && enabled && initialization,
      staleTime: 5 * 60 * 1000,
    }
  )
}

export const useVerifyEmail = () => {
  return useMutation([], async (payload: { email: string }) => {
    const res = await httpService.Post(userVerifyEmailPath, payload)
    return res as { status: Status; message: string }
  })
}

export const useDuprIdDetails = (claimId: string) => {
  return useQuery({
    queryKey: ['playerDetails', claimId],
    queryFn: async () => {
      const res = await httpService.Post<{
        status: Status
        message?: string
        errors: Array<{ message: string }>
        results: Array<{
          name: string
          gender?: string
          age?: number
          id: string
          userId: string
        }>
      }>(`/player/v1.0/claim/details`, { duprId: claimId })

      if (res.status === 'FAILURE') {
        throw new Error(res?.errors[0]?.message || 'Failed to fetch player details')
      }
      return res
    },
  })
}
